import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, forkJoin, of, throwError, timer } from "rxjs";
import {
  catchError,
  expand,
  filter,
  map,
  switchMap,
  takeWhile,
  tap,
} from "rxjs/operators";
import { ToastrService } from "../shared/toastr.service";
import { DigitalCardService } from "./digital-card.service";
import {
  DELETE_DIGITAL_CARD_PROFILE,
  DELETE_DIGITAL_CARD_PROFILE_SUCCESS,
  DeleteDigitalCardProfileAction,
  DeleteDigitalCardProfileFailureAction,
  DeleteDigitalCardProfileSuccessAction,
  GET_DIGITAL_CARD_EMPLOYEE_PROFILES,
  GET_DIGITAL_CARD_PROFILES,
  GetDigitalCardEmployeeProfilesAction,
  GetDigitalCardEmployeeProfilesFailureAction,
  GetDigitalCardEmployeeProfilesSuccessAction,
  GetDigitalCardProfilesAction,
  GetDigitalCardProfilesFailureAction,
  GetDigitalCardProfilesSuccessAction,
  SET_DEFAULT_DIGITAL_CARD_PROFILE,
  SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS,
  SetDefaultDigitalCardProfileAction,
  SetDefaultDigitalCardProfileFailureAction,
  SetDefaultDigitalCardProfileSuccessAction,
} from "./digital-cards.actions";

const POLLING_INTERVAL_MS = 5000;
const POLLING_MAX_INTERVAL_MS = 20000;
const POLLING_MAX_ERRORS = 5;

@Injectable()
export class DigitalCardsEffects {
  getDigitalCardProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_DIGITAL_CARD_PROFILES),
      switchMap((action: GetDigitalCardProfilesAction) =>
        this.digitalCardService
          .getProfiles({
            templatesOnly: action.payload.templatesOnly,
            mine: action.payload.mine,
          })
          .pipe(
            switchMap((res) => [
              new GetDigitalCardProfilesSuccessAction(res.profiles ?? []),
            ]),
            catchError((err) =>
              of(new GetDigitalCardProfilesFailureAction(err)),
            ),
          ),
      ),
    ),
  );

  getDigitalCardEmployeeProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_DIGITAL_CARD_EMPLOYEE_PROFILES),
      switchMap((action: GetDigitalCardEmployeeProfilesAction) => {
        const { templateId, mine } = action.payload;
        let errorCount = 0;
        let intervalMs = POLLING_INTERVAL_MS;
        const pollOnce = () =>
          forkJoin({
            readiness: this.digitalCardService.getProfileReadiness(
              templateId,
              mine,
            ),
            profiles: this.digitalCardService.getProfiles({
              forTemplate: templateId,
            }),
          }).pipe(
            tap(() => {
              errorCount = 0;
              intervalMs = POLLING_INTERVAL_MS;
            }),
            catchError((err: unknown) => {
              const status =
                err && typeof err === "object" && "status" in err
                  ? Number((err as { status?: number }).status)
                  : NaN;
              errorCount += 1;
              if (status === 429 || errorCount >= POLLING_MAX_ERRORS) {
                return throwError(() => err);
              }
              intervalMs = Math.min(
                POLLING_MAX_INTERVAL_MS,
                Math.round(intervalMs * 1.5),
              );
              return of(null);
            }),
          );
        return pollOnce().pipe(
          expand((result) => {
            if (result == null || result.readiness.status === "pending") {
              return timer(intervalMs).pipe(switchMap(() => pollOnce()));
            }
            return EMPTY;
          }),
          takeWhile(
            (result) => result == null || result.readiness.status === "pending",
            true,
          ),
          filter(
            (result): result is NonNullable<typeof result> => result != null,
          ),
          map(
            (result) =>
              new GetDigitalCardEmployeeProfilesSuccessAction({
                templateId,
                profiles: result.profiles.profiles ?? [],
                readiness: result.readiness.status,
                expected: result.readiness.expected,
                synced: result.readiness.synced,
              }),
          ),
          catchError((err) =>
            of(
              new GetDigitalCardEmployeeProfilesFailureAction({
                templateId,
                error: err,
              }),
            ),
          ),
        );
      }),
    ),
  );

  deleteDigitalCardProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_DIGITAL_CARD_PROFILE),
      switchMap((action: DeleteDigitalCardProfileAction) =>
        this.digitalCardService.deleteProfile(action.payload).pipe(
          map(() => new DeleteDigitalCardProfileSuccessAction(action.payload)),
          catchError((err) =>
            of(new DeleteDigitalCardProfileFailureAction(err)),
          ),
        ),
      ),
    ),
  );

  setDefaultDigitalCardProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_DEFAULT_DIGITAL_CARD_PROFILE),
      switchMap((action: SetDefaultDigitalCardProfileAction) =>
        this.digitalCardService.setDefaultProfile(action.payload).pipe(
          map(
            () => new SetDefaultDigitalCardProfileSuccessAction(action.payload),
          ),
          catchError((err) =>
            of(new SetDefaultDigitalCardProfileFailureAction(err)),
          ),
        ),
      ),
    ),
  );

  deleteDigitalCardProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DELETE_DIGITAL_CARD_PROFILE_SUCCESS),
        tap(() => {
          this.toastr.success(
            this.translate.instant("digitalCards.deleted") as string,
          );
        }),
      ),
    { dispatch: false },
  );

  setDefaultDigitalCardProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS),
        tap(() => {
          this.toastr.success(
            this.translate.instant(
              "digitalCards.default.setDefaultSuccess",
            ) as string,
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private digitalCardService: DigitalCardService,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {}
}
